import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

import { Plus, Trash2, Pencil, Info, Landmark, TrendingDown, PiggyBank, CalendarIcon } from "lucide-react";
import { useLoans, LoanFormData } from "@/hooks/useLoans";
import { useExpenses, DEFAULT_EXPENSE_TYPES } from "@/hooks/useExpenses";
import { supabase } from "@/integrations/supabase/client";

import { useVehicles } from "@/hooks/useVehicles";
import { format, addMonths, parseISO } from "date-fns";
import { getScheduledDates } from "@/lib/dateUtils";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const LoansTab = () => {
  const { loans, isLoading, addLoan, updateLoan, deleteLoan, addLoanWithBackfill } = useLoans();
  const { expenses, expenseTypes, paidByOptions } = useExpenses();
  const { vehicles } = useVehicles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [vehicleValues, setVehicleValues] = useState<Record<string, number>>({});
  const [initialized, setInitialized] = useState(false);

  // Initialize vehicleValues from vehicles' kbb_value
  useEffect(() => {
    if (!vehicles || loans.length === 0) return;
    const initial: Record<string, number> = {};
    for (const loan of loans) {
      if (loan.vehicle_id) {
        const v = vehicles.find((v) => v.id === loan.vehicle_id);
        if (v && (v as any).kbb_value) {
          initial[loan.id] = Number((v as any).kbb_value);
        }
      }
    }
    setVehicleValues(initial);
    setInitialized(true);
  }, [vehicles, loans]);

  // Save kbb_value to vehicle when user changes the value
  const saveKbbValue = useCallback(async (loanId: string, vehicleId: string | null, value: number) => {
    if (!vehicleId) return;
    await supabase
      .from("vehicles")
      .update({ kbb_value: value } as any)
      .eq("id", vehicleId);
  }, []);
  const [form, setForm] = useState<LoanFormData>({
    lender_name: "",
    original_amount: 0,
    interest_rate: 0,
    term_months: 60,
    origination_date: new Date().toISOString().split("T")[0],
    monthly_payment: 0,
    expense_type: "Loans",
  });

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewBackfill, setPreviewBackfill] = useState<string[]>([]);
  const [previewUpcoming, setPreviewUpcoming] = useState<string[]>([]);
  const [pendingForm, setPendingForm] = useState<LoanFormData | null>(null);
  const [pendingInterest, setPendingInterest] = useState(0);
  const [pendingPrincipal, setPendingPrincipal] = useState(0);

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const pct = (n: number) => `${n.toFixed(2)}%`;

  const resetForm = () => {
    setForm({
      lender_name: "",
      original_amount: 0,
      interest_rate: 0,
      term_months: 60,
      origination_date: new Date().toISOString().split("T")[0],
      monthly_payment: 0,
      expense_type: "Loans",
    });
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (loan: typeof loans[0]) => {
    setForm({
      vehicle_id: loan.vehicle_id,
      lender_name: loan.lender_name,
      original_amount: loan.original_amount,
      interest_rate: loan.interest_rate,
      term_months: loan.term_months,
      origination_date: loan.origination_date,
      monthly_payment: loan.monthly_payment,
      expense_type: loan.expense_type,
      paid_by: loan.paid_by,
      paid_to: loan.paid_to,
    });
    setEditingId(loan.id);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      // Edit — save directly, no backfill preview
      updateLoan.mutate({ id: editingId, ...form });
      setDialogOpen(false);
      resetForm();
    } else {
      // Creating — compute interest/principal split and show confirmation
      const monthlyRate = form.interest_rate / 100 / 12;
      const interestAmt = Math.round(form.original_amount * monthlyRate * 100) / 100;
      const principalAmt = Math.round((form.monthly_payment - interestAmt) * 100) / 100;

      const { backfill, upcoming } = getScheduledDates(form.origination_date, "monthly");

      setPendingForm(form);
      setPendingInterest(interestAmt);
      setPendingPrincipal(principalAmt);
      setPreviewBackfill(backfill);
      setPreviewUpcoming(upcoming);
      setDialogOpen(false);
      setConfirmOpen(true);
    }
  };

  const handleConfirmLoan = () => {
    if (!pendingForm) return;
    addLoanWithBackfill.mutate({
      loanData: pendingForm,
      backfillDates: previewBackfill,
      interestAmount: pendingInterest,
      principalAmount: pendingPrincipal,
    });
    setConfirmOpen(false);
    setPendingForm(null);
    resetForm();
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setDialogOpen(true); // re-open the form
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this loan and its recurring expense templates?")) {
      deleteLoan.mutate(id);
    }
  };

  const getVehicleName = (vehicleId: string | null) => {
    if (!vehicleId) return "Operations";
    const v = vehicles?.find((v) => v.id === vehicleId);
    return v ? v.name || `${v.year} ${v.make} ${v.model}` : "Unknown";
  };

  // Calculate outstanding balances dynamically from expenses
  const loanBalances = useMemo(() => {
    return loans.map((loan) => {
      const principalPaid = expenses
        .filter((e) => (e as any).loan_payment_type === "Principal Paydown" && (e as any).loan_id === loan.id)
        .reduce((sum, e) => sum + Number(e.amount || 0), 0);

      const outstandingBalance = Math.max(0, loan.original_amount - principalPaid);
      const vehicleValue = vehicleValues[loan.id] ?? 0;
      const equity = vehicleValue - outstandingBalance;

      return { loan, principalPaid, outstandingBalance, vehicleValue, equity };
    });
  }, [loans, expenses, vehicleValues]);

  // Amortization chart data for all loans combined
  const amortizationData = useMemo(() => {
    if (loans.length === 0) return [];

    const data: { month: string; balance: number }[] = [];
    const today = new Date();

    for (const lb of loanBalances) {
      const { loan, outstandingBalance } = lb;
      const monthlyRate = loan.interest_rate / 100 / 12;
      let balance = outstandingBalance;
      const monthlyPrincipal = loan.monthly_payment - balance * monthlyRate;

      for (let i = 0; i <= Math.min(loan.term_months, 120); i++) {
        const date = addMonths(today, i);
        const label = format(date, "MMM yy");

        const existing = data.find((d) => d.month === label);
        if (existing) {
          existing.balance += Math.max(0, balance);
        } else {
          data.push({ month: label, balance: Math.max(0, balance) });
        }

        const interest = balance * monthlyRate;
        const principal = Math.min(loan.monthly_payment - interest, balance);
        balance = Math.max(0, balance - principal);
      }
    }

    return data;
  }, [loans, loanBalances]);

  const totalOutstanding = loanBalances.reduce((s, lb) => s + lb.outstandingBalance, 0);
  const totalEquity = loanBalances.reduce((s, lb) => s + lb.equity, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Cross-Tab Effect Alert */}
        <Alert className="border-primary/20 bg-primary/5">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm text-muted-foreground">
            Creating a loan automatically generates two recurring templates in the <strong>Recurring</strong> tab — one for Interest and one for Principal Paydown. To make extra payments, increase the principal amount in the Recurring tab. The outstanding balance below updates dynamically from actual transactions in the <strong>Expenses</strong> tab.
          </AlertDescription>
        </Alert>

        {/* ─── Top Section: Loan Data ─── */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Loans</h2>
            <Button onClick={openAdd} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Loan
            </Button>
          </div>

          {loans.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-10 text-center">
                <Landmark className="h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-sm">No loans yet. Add a loan to track vehicle financing.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold text-xs">Lender</TableHead>
                    <TableHead className="font-semibold text-xs">Vehicle</TableHead>
                    <TableHead className="font-semibold text-xs">Original Amount</TableHead>
                    <TableHead className="font-semibold text-xs">APR</TableHead>
                    <TableHead className="font-semibold text-xs">Term</TableHead>
                    <TableHead className="font-semibold text-xs">Monthly Payment</TableHead>
                    <TableHead className="font-semibold text-xs">Status</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map((loan) => (
                    <TableRow key={loan.id} className="group">
                      <TableCell className="font-medium">{loan.lender_name}</TableCell>
                      <TableCell>{getVehicleName(loan.vehicle_id)}</TableCell>
                      <TableCell>{fmt(loan.original_amount)}</TableCell>
                      <TableCell>{pct(loan.interest_rate)}</TableCell>
                      <TableCell>{loan.term_months} months</TableCell>
                      <TableCell>{fmt(loan.monthly_payment)}</TableCell>
                      <TableCell>
                        <Badge variant={loan.status === "active" ? "default" : "secondary"}>
                          {loan.status === "active" ? "Active" : "Paid Off"}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-1">
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(loan)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(loan.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* ─── Middle Section: Equity Dashboard ─── */}
        {loans.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Equity Dashboard</h2>

            {/* Summary Cards */}
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
              <Card className="border-border/50 bg-gradient-to-br from-card to-card/80">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Outstanding</CardTitle>
                  <TrendingDown className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">{fmt(totalOutstanding)}</div>
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-gradient-to-br from-card to-card/80">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Equity</CardTitle>
                  <PiggyBank className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${totalEquity >= 0 ? "text-green-600" : "text-destructive"}`}>
                    {fmt(totalEquity)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Set vehicle values below to calculate
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-gradient-to-br from-card to-card/80">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active Loans</CardTitle>
                  <Landmark className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-foreground">
                    {loans.filter((l) => l.status === "active").length}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Per-Loan Equity Breakdown */}
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {loanBalances.map(({ loan, outstandingBalance, equity }) => (
                <Card key={loan.id} className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">
                      {loan.lender_name}
                      {loan.vehicle_id && (
                        <span className="text-muted-foreground font-normal"> — {getVehicleName(loan.vehicle_id)}</span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Outstanding: <span className="font-semibold text-foreground">{fmt(outstandingBalance)}</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-end gap-3">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs text-muted-foreground flex items-center gap-1">
                          Est. Vehicle Value
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-3 w-3" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              Enter the vehicle's current market value. Equity = Vehicle Value − Outstanding Balance. Overpaying via the Recurring tab creates larger principal expenses, making this balance drop faster.
                            </TooltipContent>
                          </Tooltip>
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step={100}
                          placeholder="e.g. 25000"
                          value={vehicleValues[loan.id] ?? ""}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setVehicleValues((prev) => ({
                              ...prev,
                              [loan.id]: val,
                            }));
                          }}
                          onBlur={() => saveKbbValue(loan.id, loan.vehicle_id, vehicleValues[loan.id] ?? 0)}
                        />
                      </div>
                      <div className="text-right pb-1">
                        <p className="text-xs text-muted-foreground">Equity</p>
                        <p className={`text-lg font-bold ${equity >= 0 ? "text-green-600" : "text-destructive"}`}>
                          {fmt(equity)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ─── Bottom Section: Amortization Chart ─── */}
        {loans.length > 0 && amortizationData.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Amortization Projection</h2>
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <ChartContainer
                  config={{
                    balance: { label: "Outstanding Balance", color: "hsl(var(--primary))" },
                  }}
                  className="h-[300px] w-full"
                >
                  <AreaChart data={amortizationData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                      className="text-muted-foreground"
                    />
                    <YAxis
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 11 }}
                      className="text-muted-foreground"
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area
                      type="monotone"
                      dataKey="balance"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary) / 0.15)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── New Loan Dialog ─── */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit" : "New"} Loan</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update loan details. The associated recurring expense templates will be updated automatically."
                  : "Add a vehicle loan. This will create two recurring expense templates (Interest & Principal Paydown) in the Recurring tab."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Lender Name</Label>
                <Input
                  value={form.lender_name}
                  onChange={(e) => setForm({ ...form, lender_name: e.target.value })}
                  placeholder="e.g. Capital One Auto Finance"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle</Label>
                  <Select
                    value={form.vehicle_id || "operations"}
                    onValueChange={(v) => setForm({ ...form, vehicle_id: v === "operations" ? null : v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operations">Operations (No Vehicle)</SelectItem>
                      {vehicles?.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name || `${v.year} ${v.make} ${v.model}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Expense Type</Label>
                  <Select
                    value={form.expense_type || ""}
                    onValueChange={(v) => setForm({ ...form, expense_type: v || null })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {expenseTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Original Amount ($)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={100}
                    value={form.original_amount || ""}
                    onChange={(e) => setForm({ ...form, original_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>APR (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.interest_rate || ""}
                    onChange={(e) => setForm({ ...form, interest_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Term (months)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={form.term_months || ""}
                    onChange={(e) => setForm({ ...form, term_months: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Monthly Payment ($)</Label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.monthly_payment || ""}
                    onChange={(e) => setForm({ ...form, monthly_payment: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Origination Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-9 px-3",
                        !form.origination_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.origination_date
                        ? format(parseISO(form.origination_date), "MMM d, yyyy")
                        : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-popover" align="start">
                    <Calendar
                      mode="single"
                      selected={form.origination_date ? parseISO(form.origination_date) : undefined}
                      defaultMonth={form.origination_date ? parseISO(form.origination_date) : undefined}
                      onSelect={(date) => {
                        if (date) setForm({ ...form, origination_date: format(date, "yyyy-MM-dd") });
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Paid By</Label>
                  <Select
                    value={form.paid_by || ""}
                    onValueChange={(v) => setForm({ ...form, paid_by: v || null })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {paidByOptions.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Paid To</Label>
                  <Input
                    value={form.paid_to || ""}
                    onChange={(e) => setForm({ ...form, paid_to: e.target.value || null })}
                    placeholder="e.g. Capital One"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={!form.lender_name || !form.original_amount || !form.monthly_payment}
              >
                {editingId ? "Save Changes" : "Create Loan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Loan Backfill Confirmation Dialog */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Confirm Loan Creation</DialogTitle>
              <DialogDescription>
                Review the payments that will be generated for this loan.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Backfill section */}
              {previewBackfill.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    Expenses to create now
                  </h3>
                  <div className="border rounded-md">
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="text-xs font-semibold">Date</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Interest</TableHead>
                            <TableHead className="text-xs font-semibold text-right">Principal</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {previewBackfill.map((date) => (
                            <TableRow key={date}>
                              <TableCell className="py-1.5 text-sm">
                                {format(new Date(date + "T00:00:00"), "MMM d, yyyy")}
                              </TableCell>
                              <TableCell className="py-1.5 text-sm text-right">
                                {fmt(pendingInterest)}
                              </TableCell>
                              <TableCell className="py-1.5 text-sm text-right">
                                {fmt(pendingPrincipal)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {previewBackfill.length} month{previewBackfill.length === 1 ? "" : "s"} · {previewBackfill.length * 2} expenses · {fmt(previewBackfill.length * (pendingInterest + pendingPrincipal))} total
                  </p>
                </div>
              )}

              {/* Upcoming section */}
              {previewUpcoming.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground">
                    Upcoming scheduled payments (auto-generated)
                  </h3>
                  <div className="border rounded-md border-dashed">
                    <Table>
                      <TableBody>
                        {previewUpcoming.map((date) => (
                          <TableRow key={date} className="text-muted-foreground">
                            <TableCell className="py-1.5 text-sm">
                              {format(new Date(date + "T00:00:00"), "MMM d, yyyy")}
                            </TableCell>
                            <TableCell className="py-1.5 text-sm text-right">
                              {fmt(pendingInterest)}
                            </TableCell>
                            <TableCell className="py-1.5 text-sm text-right">
                              {fmt(pendingPrincipal)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    These will be created automatically on their due dates.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancelConfirm}>Cancel</Button>
              <Button
                onClick={handleConfirmLoan}
                disabled={addLoanWithBackfill.isPending}
              >
                {addLoanWithBackfill.isPending ? "Creating…" : "Confirm & Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};
